<template>
  <div class="py-12 bg-white">
    <div class="max-w-5xl mx-auto px-4">
      <!-- Header -->
      <div class="text-center mb-6">
        <h2 class="text-3xl font-light text-primary mb-2">
          {{ content.title }}
        </h2>
        <p class="text-gray-600">
          {{ content.subtitle }}
          <a
            v-if="content.linkText"
            :href="content.linkUrl || '#'"
            class="text-primary font-semibold hover:underline ml-1"
          >
            {{ content.linkText }} &rarr;
          </a>
        </p>
      </div>

      <!-- Scrollable cards -->
      <div class="relative">
        <div
          ref="scrollContainer"
          class="flex gap-4 overflow-x-auto pb-4 scroll-smooth hide-scrollbar"
        >
          <div
            v-for="(group, i) in content.groups"
            :key="i"
            class="min-w-[180px] max-w-[200px] flex-shrink-0 cursor-pointer group"
            @click="selectedIndex = i"
          >
            <!-- Image or placeholder -->
            <div
              class="rounded-lg aspect-square flex items-center justify-center mb-3 overflow-hidden transition-all"
              :class="selectedIndex === i ? 'border-2 border-primary bg-blue-100' : 'border-2 border-transparent bg-gray-200'"
            >
              <img
                v-if="group.imageUrl"
                v-lazy-load :data-src="group.imageUrl"
                :alt="group.name"
                class="w-full h-full object-cover"
              />
              <svg
                v-else
                class="w-12 h-12 transition-colors"
                :class="selectedIndex === i ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500'"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
                />
              </svg>
            </div>
            <!-- Category name -->
            <p
              class="text-sm font-medium transition-colors"
              :class="selectedIndex === i ? 'text-primary font-bold' : 'text-gray-800 group-hover:text-primary'"
            >
              {{ group.name }}
            </p>
          </div>
        </div>

        <!-- Navigation -->
        <div class="flex items-center gap-2 mt-4">
          <button
            class="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 hover:text-gray-600"
            @click="scroll(-1)"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            class="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90"
            @click="scroll(1)"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
          <div
            class="flex-1 h-0.5 bg-gray-200 rounded-full overflow-hidden"
          >
            <div
              class="h-full bg-primary rounded-full transition-all"
              :style="{ width: scrollProgress + '%' }"
            />
          </div>
        </div>

        <!-- Description -->
        <div
          v-if="selectedGroup && selectedGroup.description"
          class="mt-6 p-4 bg-blue-50 rounded-lg"
        >
          <h3 class="text-lg font-semibold text-gray-900 mb-1">
            {{ selectedGroup.name }}
          </h3>
          <p class="text-gray-600">
            {{ selectedGroup.description }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  useContext,
} from "@nuxtjs/composition-api";
import useModuleContent from "~/composables/useModuleContent";

export default {
  name: "ProfessionGroupsModule",
  props: {
    moduleConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { $posthog } = useContext();
    const { content } = useModuleContent(props);

    const defaultIndex = computed(() => {
      const idx = parseInt(content.value.defaultGroup, 10);
      return isNaN(idx) ? 0 : idx;
    });
    const selectedIndex = ref(0);
    const selectedGroup = computed(
      () => (content.value.groups || [])[selectedIndex.value] || null
    );

    const scrollContainer = ref(null);
    const scrollProgress = ref(20);

    const updateProgress = () => {
      const el = scrollContainer.value;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      scrollProgress.value = max > 0 ? (el.scrollLeft / max) * 80 + 20 : 20;
    };

    const scroll = (dir) => {
      const el = scrollContainer.value;
      if (!el) return;
      el.scrollBy({ left: dir * 220, behavior: "smooth" });
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          updateProgress();
          ticking = false;
        });
      }
    };

    onMounted(() => {
      selectedIndex.value = defaultIndex.value;

      if (scrollContainer.value) {
        scrollContainer.value.addEventListener("scroll", onScroll);
      }

      $posthog?.capture("front_page_module_viewed", {
        module_slug: props.moduleConfig.slug,
        module_position: props.moduleConfig.position,
        module_row: props.moduleConfig.row,
        version_id: props.moduleConfig.versionId,
      });
    });

    onBeforeUnmount(() => {
      if (scrollContainer.value) {
        scrollContainer.value.removeEventListener("scroll", onScroll);
      }
    });

    return {
      content,
      scrollContainer,
      scrollProgress,
      scroll,
      selectedIndex,
      selectedGroup,
    };
  },
};
</script>

<style scoped>
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
